import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import * as Realm from "realm-web";

const History = ({ setLoggedIn }) => {
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
  const [currentEmployee, setCurrentEmployee] = useState();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  
  const dropdownRef = useRef(null);
  const calendarRef = useRef(null);

  const token = localStorage.getItem('accessToken');
  const tokenCompany = localStorage.getItem('company');
  const navigate = useNavigate();
  const app = new Realm.App({ id: "data-ywwpoom" });
  const stayLoggedIn = localStorage.getItem('stayLoggedIn') === 'true';

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const validateAndRefresh = async () => {
      try {
        const isValid = await validateToken(token);
        if (!isValid) {
          if (stayLoggedIn) {
            const refreshed = await refreshAccessToken();
            if (!refreshed) {
              handleLogout();
            } else {
              const newToken = app.currentUser.accessToken;
              const isValidNewToken = await validateToken(newToken);
              if (!isValidNewToken) {
                handleLogout();
              } else {
                setLoggedIn(true);
              }
            }
          } else {
            handleLogout();
          }
        } else {
          setLoggedIn(true);
        }
      } catch (error) {
        console.error('Token validation error:', error);
        handleLogout();
      }
    };
    
    validateAndRefresh();
  }, [token]);

  const handleLogout = () => {
    setLoggedIn(false);
    navigate('/login');
  };

  const validateToken = (token) => {
    return new Promise((resolve, reject) => {
      fetch(process.env.REACT_APP_EMPLOYEE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Request-Headers': '*',
        },
        body: JSON.stringify({ token }),
        redirect: 'follow'
      })
        .then(response => response.json())
        .then(result => resolve(!result.error))
        .catch(error => reject(error));
    });
  };

  const refreshAccessToken = async () => {
    try {
      await app.currentUser.refreshAccessToken();
      localStorage.setItem('accessToken', app.currentUser.accessToken);
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };

  useEffect(() => {
    if (!token) return;
    
    fetch(process.env.REACT_APP_EMPLOYEE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Request-Headers': '*',
      },
      body: JSON.stringify({ token }),
      redirect: 'follow'
    })
      .then(response => response.json())
      .then(result => {
        if (!result.error) setCurrentEmployee(result);
      })
      .catch(error => console.error('Error:', error));
  }, [token]);

  useEffect(() => {
    if (!token || !currentEmployee) return;

    setLoading(true);
    fetch(process.env.REACT_APP_EMPLOYEELIST_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Request-Headers': '*',
      },
      body: JSON.stringify({ token, company: tokenCompany }),
      redirect: 'follow'
    })
      .then(response => response.json())
      .then(result => {
        if (!result.error) {
          const filteredEmployees = currentEmployee?.isSuperAdmin 
            ? result 
            : result.filter(employee => 
                currentEmployee?.subEmployees?.includes(employee.employeeId) || 
                employee.employeeId === currentEmployee?.employeeId
              );

          const sortedEmployees = [...filteredEmployees].sort((a, b) => 
            a.lastName.toUpperCase().localeCompare(b.lastName.toUpperCase())
          );
          
          setEmployeeList(sortedEmployees);
        }
      })
      .catch(error => console.error('Error:', error))
      .finally(() => setLoading(false));
  }, [token, tokenCompany, currentEmployee]);

  useEffect(() => {
    if (!selectedEmployee) return;

    setLoading(true);
    fetch('https://eu-central-1.aws.data.mongodb-api.com/app/data-ywwpoom/endpoint/getLogs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Request-Headers': '*',
      },
      body: JSON.stringify({
        token,
        employeeId: selectedEmployee.employeeId || selectedEmployee._id
      }),
      redirect: 'follow'
    })
      .then(response => response.json())
      .then(result => {
        if (!result.error) {
          if (selectedMonth) {
            const monthLogs = result.filter(log => {
              if (log.timestamp) {
                const logDate = new Date(log.timestamp);
                if (!isNaN(logDate.getTime())) {
                  const year = logDate.getFullYear();
                  const month = String(logDate.getMonth() + 1).padStart(2, '0');
                  const logMonth = `${year}-${month}`;
                  return logMonth === selectedMonth;
                }
              }
              return false;
            });
            setHistoryData(monthLogs);
          } else {
            setHistoryData(result);
          }
          setCurrentPage(1);
        }
      })
      .catch(error => console.error('Error:', error))
      .finally(() => setLoading(false));
  }, [selectedEmployee, selectedMonth, token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsEmployeeDropdownOpen(false);
      }
      
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    setCurrentPage(1);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };
  
  const formatTimestamp = (timestamp) => {
    return formatDateTime(timestamp);
  };

  const formatISODateTime = (isoString) => {
    if (!isoString) return '';
    
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return isoString;
      
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return isoString;
    }
  };

  const getActionDescription = (action) => {
    const actionMap = {
      'addTime': 'Zeit hinzugefügt',
      'updateTime': 'Zeit aktualisiert',
      'deleteTime': 'Zeit gelöscht',
      'approveTime': 'Zeit genehmigt',
      'rejectTime': 'Zeit abgelehnt',
      'addEmployee': 'Mitarbeiter hinzugefügt',
      'createEmployee': 'Mitarbeiter erstellt',
      'updateEmployee': 'Mitarbeiter aktualisiert',
      'deleteEmployee': 'Mitarbeiter gelöscht',
      'archiveEmployee': 'Mitarbeiter archiviert',
      'changeTime': 'Zeit geändert',
      'changeTimeStatus': 'Zeitstatus geändert',
      'addChangeRequest': 'Änderungsanfrage erstellt'
    };
    
    return actionMap[action] || action || 'Unbekannte Aktion';
  };

  const getStatusText = (status) => {
    if (status === 'approved') return 'Genehmigt';
    if (status === 'denied' || status === 'rejected') return 'Abgelehnt';
    return 'Ausstehend';
  };

  const getStatusClass = (status) => {
    if (status === 'approved') return 'text-green-600';
    if (status === 'denied' || status === 'rejected') return 'text-red-600';
    return 'text-yellow-600';
  };

  const getBooleanText = (value) => (value ? 'Ja' : 'Nein');
  
  const getRelevantDataField = (log) => {
    if (log.addedData) return log.addedData;
    if (log.changedData) return log.changedData;
    if (log.deletedData) return log.deletedData;
    if (log.requestData) return log.requestData;
    if (log.data) return log.data;
    return null;
  };

  const renderLogData = (log) => {
    const data = getRelevantDataField(log);
    
    if (!data) return <p className="italic text-gray-500">Keine Detaildaten verfügbar</p>;

    if (log.action === 'createEmployee' || log.action === 'addEmployee') {
      return (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div>
            <span className="font-medium">Mitarbeiter ID:</span>{' '}
            <span className="text-xs text-gray-500">{data.employeeId}</span>
          </div>
          <div>
            <span className="font-medium">Name:</span>{' '}
            {data.firstName} {data.lastName || ''}
            {(!data.firstName && !data.lastName && data.name) && data.name}
          </div>
          <div>
            <span className="font-medium">E-Mail:</span>{' '}
            {data.email}
          </div>
          <div>
            <span className="font-medium">Ist Super-Admin:</span>{' '}
            {getBooleanText(data.isSuperAdmin)}
          </div>
          <div>
            <span className="font-medium">Ist Admin:</span>{' '}
            {getBooleanText(data.isAdmin)}
          </div>
          <div>
            <span className="font-medium">Ist BR-Admin:</span>{' '}
            {getBooleanText(data.isBRAdmin)}
          </div>
          {data.jobTitle && (
            <div>
              <span className="font-medium">Job Titel:</span>{' '}
              {data.jobTitle}
            </div>
          )}
        </div>
      );
    }
    
    if (log.action === 'archiveEmployee') {
      let employee = data.archivedEmployee;
      
      if (typeof employee === 'string') {
        try {
          employee = JSON.parse(employee);
        } catch (error) {
          console.error('Error parsing archived employee data:', error);
        }
      }
      
      if (!employee) return <p className="italic text-gray-500">Keine Detaildaten zum archivierten Mitarbeiter verfügbar</p>;
      
      return (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="col-span-2 font-medium text-gray-700 mb-1">Archivierter Mitarbeiter:</div>
          <div>
            <span className="font-medium">Mitarbeiter ID:</span>{' '}
            <span className="text-xs text-gray-500">{employee.employeeId}</span>
          </div>
          <div>
            <span className="font-medium">Name:</span>{' '}
            {employee.firstName} {employee.lastName}
          </div>
          <div>
            <span className="font-medium">E-Mail:</span>{' '}
            {employee.email}
          </div>
          <div>
            <span className="font-medium">Job Titel:</span>{' '}
            {employee.jobTitle || 'Nicht angegeben'}
          </div>
        </div>
      );
    }
    
    if (log.action === 'deleteEmployee') {
      let employee = data.deletedEmployee;
      
      if (typeof employee === 'string') {
        try {
          employee = JSON.parse(employee);
        } catch (error) {
          console.error('Error parsing deleted employee data:', error);
        }
      }
      
      if (!employee) return <p className="italic text-gray-500">Keine Detaildaten zum gelöschten Mitarbeiter verfügbar</p>;
      
      return (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="col-span-2 font-medium text-gray-700 mb-1">Gelöschter Mitarbeiter:</div>
          <div>
            <span className="font-medium">Mitarbeiter ID:</span>{' '}
            <span className="text-xs text-gray-500">{employee.employeeId}</span>
          </div>
          <div>
            <span className="font-medium">Name:</span>{' '}
            {employee.firstName} {employee.lastName}
          </div>
          <div>
            <span className="font-medium">E-Mail:</span>{' '}
            {employee.email}
          </div>
          <div>
            <span className="font-medium">Job Titel:</span>{' '}
            {employee.jobTitle || 'Nicht angegeben'}
          </div>
        </div>
      );
    }
    
    if (log.action === 'updateEmployee') {
      let before = data.before;
      let after = data.after;
      
      if (typeof before === 'string') {
        try {
          before = JSON.parse(before);
        } catch (error) {
          console.error('Error parsing employee before data:', error);
        }
      }
      
      if (typeof after === 'string') {
        try {
          after = JSON.parse(after);
        } catch (error) {
          console.error('Error parsing employee after data:', error);
        }
      }
      
      const changes = [];
      
      if (before && after) {
        if (before.firstName !== after.firstName || before.lastName !== after.lastName) {
          changes.push({
            field: 'Name',
            from: `${before.firstName} ${before.lastName}`,
            to: `${after.firstName} ${after.lastName}`
          });
        }
        
        if (before.jobTitle !== after.jobTitle) {
          changes.push({
            field: 'Job Titel',
            from: before.jobTitle || 'Nicht angegeben',
            to: after.jobTitle || 'Nicht angegeben'
          });
        }
        
        if (before.isAdmin !== after.isAdmin) {
          changes.push({
            field: 'Admin-Status',
            from: getBooleanText(before.isAdmin),
            to: getBooleanText(after.isAdmin)
          });
        }
        
        if (before.isSuperAdmin !== after.isSuperAdmin) {
          changes.push({
            field: 'Super-Admin-Status',
            from: getBooleanText(before.isSuperAdmin),
            to: getBooleanText(after.isSuperAdmin)
          });
        }
        
        if (before.isBRAdmin !== after.isBRAdmin) {
          changes.push({
            field: 'BR-Admin-Status',
            from: getBooleanText(before.isBRAdmin),
            to: getBooleanText(after.isBRAdmin)
          });
        }
        
        if (before.timeModel !== after.timeModel) {
          changes.push({
            field: 'Zeitmodell',
            from: before.timeModel || 'Nicht angegeben',
            to: after.timeModel || 'Nicht angegeben'
          });
        }
        
        const additionalFields = [
          { key: 'hours_month', label: 'Stunden pro Monat' },
          { key: 'hours_week', label: 'Stunden pro Woche' },
          { key: 'holidayValue', label: 'Urlaubstage' },
          { key: 'contractualAverageWorkingTime', label: 'Vertragliche Arbeitszeit' },
          { key: 'contractModel', label: 'Vertragsmodell' }
        ];
        
        additionalFields.forEach(field => {
          if (before[field.key] !== after[field.key]) {
            changes.push({
              field: field.label,
              from: before[field.key] || 'Nicht angegeben',
              to: after[field.key] || 'Nicht angegeben'
            });
          }
        });
      }
      
      const emailChanged = data.emailChanged === true || (data.oldEmail !== data.newEmail);
      
      return (
        <div className="mt-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="font-medium">Mitarbeiter ID:</span>{' '}
              <span className="text-xs text-gray-500">{data.employeeId}</span>
            </div>
            
            {emailChanged && (
              <>
                <div>
                  <span className="font-medium">E-Mail geändert:</span>{' '}
                  {getBooleanText(true)}
                </div>
                <div>
                  <span className="font-medium">Alte E-Mail:</span>{' '}
                  {data.oldEmail}
                </div>
                <div>
                  <span className="font-medium">Neue E-Mail:</span>{' '}
                  {data.newEmail}
                </div>
              </>
            )}
          </div>
          
          {changes.length > 0 && (
            <div className="mt-3">
              <div className="font-medium text-gray-700 mb-2">Änderungen:</div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-1 px-2 text-left">Feld</th>
                    <th className="py-1 px-2 text-left">Alt</th>
                    <th className="py-1 px-2 text-left">Neu</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((change, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-1 px-2">{change.field}</td>
                      <td className="py-1 px-2">{change.from}</td>
                      <td className="py-1 px-2">{change.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }

    if (['addTime', 'changeTime'].includes(log.action)) {
      return (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {renderField(data, 'date', 'Datum', false, false, true)}
          {renderField(data, 'start', 'Start', false, false, true)}
          {renderField(data, 'startTime', 'Startzeit', false, false, true)}
          {renderField(data, 'end', 'Ende', false, false, true)}
          {renderField(data, 'endTime', 'Endzeit', false, false, true)}
          {renderField(data, 'pause', 'Pause')}
          {renderField(data, 'client', 'Kunde')}
          {renderField(data, 'project', 'Projekt')}
          {renderField(data, 'task', 'Tätigkeit')}
          
          {(data.managementType || data.status) && (
            <div>
              <span className="font-medium">Status:</span>{' '}
              <span className={getStatusClass(data.managementType || data.status)}>
                {getStatusText(data.managementType || data.status)}
              </span>
            </div>
          )}
          
          {data.id && (
            <div>
              <span className="font-medium">ID:</span>{' '}
              <span className="text-xs text-gray-500">{data.id}</span>
            </div>
          )}
        </div>
      );
    } else if (log.action === 'deleteTime') {
      return (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {renderField(data, 'id', 'Gelöschte Zeit ID', true)}
          {renderField(data, 'start', 'Start', false, false, true)}
          {renderField(data, 'end', 'Ende', false, false, true)}
        </div>
      );
    } else if (log.action === 'changeTimeStatus') {
      return (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {renderField(data, 'timeId', 'Zeit ID', true)}
          <div>
            <span className="font-medium">Neuer Status:</span>{' '}
            <span className={getStatusClass(data.newStatus)}>
              {getStatusText(data.newStatus)}
            </span>
          </div>
          {renderField(data, 'comment', 'Kommentar', false, true)}
        </div>
      );
    } else if (log.action === 'addChangeRequest') {
      return (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {renderField(data, 'timeId', 'Zeit ID', true)}
          {data.newStartTime && (
            <div>
              <span className="font-medium">Neue Startzeit:</span>{' '}
              {formatISODateTime(data.newStartTime)}
            </div>
          )}
          {data.newEndTime && (
            <div>
              <span className="font-medium">Neue Endzeit:</span>{' '}
              {formatISODateTime(data.newEndTime)}
            </div>
          )}
          {renderField(data, 'comment', 'Kommentar', false, true)}
        </div>
      );
    }
    
    return (
      <div className="mt-2 grid grid-cols-2 gap-2">
        {Object.entries(data).map(([key, value]) => {
          if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
            try {
              const parsedValue = JSON.parse(value);
              return (
                <div key={key} className="col-span-2 mt-1">
                  <div className="font-medium">{key}:</div>
                  <pre className="text-xs bg-gray-50 p-2 rounded mt-1 overflow-x-auto">
                    {JSON.stringify(parsedValue, null, 2)}
                  </pre>
                </div>
              );
            } catch (e) {
              return (
                <div key={key} className="mt-1">
                  <span className="font-medium">{key}:</span>{' '}
                  {value}
                </div>
              );
            }
          }
          
          if (typeof value !== 'object') {
            return (
              <div key={key} className="mt-1">
                <span className="font-medium">{key}:</span>{' '}
                {value?.toString()}
              </div>
            );
          }
          
          return (
            <div key={key} className="col-span-2 mt-1">
              <div className="font-medium">{key}:</div>
              <pre className="text-xs bg-gray-50 p-2 rounded mt-1 overflow-x-auto">
                {JSON.stringify(value, null, 2)}
              </pre>
            </div>
          );
        })}
      </div>
    );
  };

  const renderField = (data, field, label, isId = false, fullWidth = false, isDate = false) => {
    if (!data[field]) return null;
    
    return (
      <div className={fullWidth ? "col-span-2" : ""}>
        <span className="font-medium">{label}:</span>{' '}
        {isId ? (
          <span className="text-xs text-gray-500">{data[field]}</span>
        ) : isDate ? (
          formatDateTime(data[field])
        ) : (
          data[field]
        )}
      </div>
    );
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = historyData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(historyData.length / itemsPerPage);

  const generateMonthOptions = () => {
    const today = new Date();
    const months = [];
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    
    for (let year = currentYear - 2; year <= currentYear; year++) {
      for (let month = 0; month < 12; month++) {
        if (year === currentYear && month > currentMonth) continue;
        months.push(`${year}-${String(month + 1).padStart(2, '0')}`);
      }
    }
    
    return months.sort().reverse();
  };

  const Pagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex justify-center mt-6 space-x-2">
        <button 
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded ${
            currentPage === 1 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
          }`}
        >
          &laquo; Zurück
        </button>
        
        <span className="px-3 py-1 bg-white">
          Seite {currentPage} von {totalPages}
        </span>
        
        <button 
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded ${
            currentPage === totalPages ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
          }`}
        >
          Weiter &raquo;
        </button>
      </div>
    );
  };

  const renderHistoryData = () => {
    if (historyData.length === 0) {
      return <p className="text-gray-500 italic">Keine Daten verfügbar für den ausgewählten Zeitraum.</p>;
    }

    return (
      <div className="overflow-x-auto bg-white rounded-lg shadow-lg p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Aktivitätshistorie</h2>
          <div className="text-sm text-gray-600">
            {historyData.length} Einträge gefunden
          </div>
        </div>
        
        <div className="space-y-6">
          {currentItems.map((log, index) => (
            <div key={log._id || index} className="border-b pb-4 last:border-b-0">
              <div className="flex flex-wrap justify-between items-start">
                <div className="mb-2">
                  <span className="font-bold">{getActionDescription(log.action)}</span>
                  <span className="text-gray-500 ml-2">
                    {formatTimestamp(log.timestamp)}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  <span>Von: {log.logUser?.email || 'Unbekannt'}</span>
                  {log.logUser?.employeeId && (
                    <span className="ml-2">(ID: {log.logUser.employeeId})</span>
                  )}
                </div>
              </div>
              {renderLogData(log)}
            </div>
          ))}
        </div>
        
        <Pagination />
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen pt-[48px] px-[83px]">
      {loading ? (
        <div className="flex justify-center items-center h-[75vh]">
          <Oval
            height={80}
            width={80}
            color="#0000FF"
            wrapperStyle={{}}
            wrapperClass=""
            visible={true}
            ariaLabel="oval-loading"
            secondaryColor="#0000FF"
            strokeWidth={2}
            strokeWidthSecondary={2}
          />
        </div>
      ) : (
        <>
          <div className="mb-[32px]">
            <h1 className="text-3xl font-bold mb-6 text-blue-700">Historie</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white rounded-lg shadow-lg p-6">
              <div className="relative" ref={dropdownRef}>
                <label htmlFor="employee-select" className="block text-gray-700 mb-2 font-medium">
                  Mitarbeiter
                </label>
                <div className="relative">
                  <div 
                    className="relative cursor-pointer"
                    onClick={() => setIsEmployeeDropdownOpen(true)}
                  >
                    <input
                      type="text"
                      className="w-full p-2 pr-10 border border-gray-300 rounded cursor-pointer"
                      placeholder="Mitarbeiter suchen oder auswählen..."
                      value={employeeSearchQuery}
                      onChange={(e) => {
                        setEmployeeSearchQuery(e.target.value);
                        setIsEmployeeDropdownOpen(true);
                      }}
                    />
                    <button
                      className="absolute inset-y-0 right-0 px-2 flex items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEmployeeDropdownOpen(!isEmployeeDropdownOpen);
                      }}
                    >
                      <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  {isEmployeeDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 max-h-96 overflow-auto bg-white border border-gray-300 rounded shadow-lg translate-y-1">
                      {employeeList
                        .filter(employee =>
                          `${employee.lastName} ${employee.firstName}`
                            .toLowerCase()
                            .includes(employeeSearchQuery.toLowerCase()) ||
                          `${employee.firstName} ${employee.lastName}`
                            .toLowerCase()
                            .includes(employeeSearchQuery.toLowerCase())
                        )
                        .map((employee) => (
                          <div
                            key={employee._id || employee.employeeId}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setSelectedEmployee(employee);
                              setEmployeeSearchQuery(`${employee.lastName} ${employee.firstName}`);
                              setIsEmployeeDropdownOpen(false);
                              setSelectedMonth('');
                              setHistoryData([]);
                            }}
                          >
                            {employee.lastName} {employee.firstName}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="relative" ref={calendarRef}>
                <label htmlFor="month-select" className="block text-gray-700 mb-2 font-medium">
                  Monat
                </label>
                <div className="relative">
                  <input
                    type="text"
                    className="w-full p-2 pr-10 border border-gray-300 rounded cursor-pointer"
                    placeholder="Monat auswählen..."
                    value={selectedMonth ? new Date(`${selectedMonth}-01`).toLocaleString('de-DE', { year: 'numeric', month: 'long' }) : ''}
                    onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                    readOnly
                  />
                  <button
                    className="absolute inset-y-0 right-0 px-2 flex items-center"
                    onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                  >
                    <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                {isCalendarOpen && (
                  <div className="absolute z-10 mt-1 bg-white border border-gray-300 rounded shadow-lg p-4 w-full max-w-xs">
                    <div className="flex justify-between items-center mb-4">
                      <button
                        onClick={() => {
                          const newDate = new Date(selectedCalendarDate);
                          newDate.setFullYear(newDate.getFullYear() - 1);
                          setSelectedCalendarDate(newDate);
                        }}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <div className="font-semibold">
                        {selectedCalendarDate.getFullYear()}
                      </div>
                      <button
                        onClick={() => {
                          const newDate = new Date(selectedCalendarDate);
                          newDate.setFullYear(newDate.getFullYear() + 1);
                          setSelectedCalendarDate(newDate);
                        }}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from({ length: 12 }, (_, i) => {
                        const monthDate = new Date(selectedCalendarDate.getFullYear(), i, 1);
                        const monthString = `${selectedCalendarDate.getFullYear()}-${String(i + 1).padStart(2, '0')}`;
                        const isSelected = monthString === selectedMonth;
                        
                        return (
                          <div
                            key={i}
                            onClick={() => {
                              handleMonthChange(monthString);
                              setIsCalendarOpen(false);
                            }}
                            className={`text-center p-2 rounded cursor-pointer ${
                              isSelected
                                ? 'bg-blue-500 text-white'
                                : 'hover:bg-blue-50'
                            }`}
                          >
                            {monthDate.toLocaleString('de-DE', { month: 'short' })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {selectedEmployee && selectedMonth && renderHistoryData()}
        </>
      )}
    </div>
  );
};

export default History; 